const AFFILIATE_ID = 'affiliateId';
const AFFILIATE_ID_KEYS = ['mid', 'affiliate_id', 'affiliateId'];

/**
 * The tag captured in this document, mirrored in memory.
 *
 * Capture runs from each app's entry before React renders, so an unguarded
 * storage call there would abort the bootstrap and leave a blank page - and it
 * would do so only on affiliate traffic, which is the traffic this exists to
 * serve. The mirror also keeps attribution working in the browsers that refuse
 * storage access outright, where the read would always come back empty.
 */
let capturedAffiliateId: string | undefined;

function readStoredAffiliateId(): string | undefined {
  try {
    return window.sessionStorage.getItem(AFFILIATE_ID) || undefined;
  } catch {
    return undefined;
  }
}

function writeStoredAffiliateId(affiliateId: string): void {
  try {
    window.sessionStorage.setItem(AFFILIATE_ID, affiliateId);
  } catch {
    // Private-mode storage failures must not block the app from rendering.
  }
}

// Called once from each app's entry before React renders. The tag arrives on
// the entry URL, which can be any route - a bare /?mid=... included - so
// capturing it in a route loader only covered the routes that loader happened
// to be mounted on.
export function captureAffiliateId() {
  const params = new URL(window.location.href).searchParams;
  for (const key of AFFILIATE_ID_KEYS) {
    const affiliateId = params.get(key);
    if (affiliateId) {
      capturedAffiliateId = affiliateId;
      writeStoredAffiliateId(affiliateId);
      return;
    }
  }
}

// The entry URL is the only source: the tag is captured at startup, and reading
// it never consults the location, so a later URL cannot replace the captured
// attribution. Session storage carries it across documents in the same tab; the
// memory mirror covers the document it was captured in when storage is refused.
export function getAffiliateId() {
  return capturedAffiliateId ?? readStoredAffiliateId();
}

export function removeAffiliateId() {
  capturedAffiliateId = undefined;
  try {
    window.sessionStorage.removeItem(AFFILIATE_ID);
  } catch {
    // Nothing to clear if storage was never reachable.
  }
}
